import { ChipXs } from '@app/components/ChipXs';
import { LTime } from '@app/components/date/LTime';
import { getTopicDisplayTitle } from '@app/modules/logger/screens/stream/features/live-topics/helpers/get-topic-display-title';
import { getCategoryColor } from '@app/modules/logger/screens/stream/features/live-topics/shared/topic-category-chip/category-colors';
import { TopicCategoryChip } from '@app/modules/logger/screens/stream/features/live-topics/shared/topic-category-chip/TopicCategoryChip';
import { LiveTopic } from '../../live-topics-types';
import { LocaleRelativeTime } from '@mi-front/ui';
import { Box, Divider, keyframes, Typography } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';
import { useFlashOnArrival } from '../hooks/useFlashOnArrival';

/** Keyframes for two quick background flashes (transparent → light blue → transparent). */
const flashBackgroundKeyframes = keyframes`
  0%, 100% { background-color: transparent; }
  25% { background-color: rgba(33, 150, 243, 0.12); }
  50% { background-color: transparent; }
  75% { background-color: rgba(33, 150, 243, 0.12); }
`;

const flashBackgroundAnimation = `${flashBackgroundKeyframes} 2s ease-in-out 1`;

const panelItemNewChipMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.panel.itemNew',
  defaultMessage: 'Nuevo',
});

const panelItemDevelopingChipMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.panel.itemDeveloping',
  defaultMessage: 'En desarrollo',
});

/**
 * Single list row in the Live Topics panel: header ({minutes ago} - {duration} // {category}) and title (max 2 lines).
 * Clickable; separated from next item by a divider (caller renders divider after each item).
 * When animateOnMount is true, flashes the background light blue twice on mount (used for topics that appear after initial load).
 * When showNewChip is true, a "Nuevo" chip is shown next to the time (hidden once user opens detail or topic is > 15 min).
 * When topic.is_open is true, an "En desarrollo" (Developing) chip is shown.
 */
export function LiveTopicsPanelItem({
  topic,
  onClick,
  showNewChip = false,
  isActive = false,
  isNew = false,
}: {
  topic: LiveTopic;
  onClick: () => void;
  showNewChip?: boolean;
  /** True when playback playhead is within this topic's time range (from logger playback store). */
  isActive?: boolean;
  /** When true, run background flash animation on mount. Parent should pass true only after initial load so new topics flash. */
  isNew?: boolean;
}) {
  const { $t } = useIntl();
  const categoryColor = getCategoryColor(topic.category);
  const shouldAnimate = useFlashOnArrival(isNew);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          border: 'none',
          borderLeft: isActive ? '4px solid' : undefined,
          borderLeftColor: isActive ? categoryColor : undefined,
          background: isActive ? 'action.focus' : 'none',
          cursor: 'pointer',
          px: 1.5,
          py: 1.25,
          ...(shouldAnimate && {
            animation: flashBackgroundAnimation,
          }),
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.75,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                typography: 'caption',
                color: 'text.secondary',
              }}
            >
              <span>
                <LTime value={topic.start_time} />
              </span>
              <Box component="span">·</Box>
              <span>
                <LocaleRelativeTime value={topic.start_time} />
              </span>
            </Box>
            {showNewChip && (
              <ChipXs label={$t(panelItemNewChipMessage)} color="info" variant="filled" />
            )}
            {topic.is_open && (
              <ChipXs
                label={$t(panelItemDevelopingChipMessage)}
                color="default"
                variant="outlined"
              />
            )}
          </Box>
          {topic.category && (
            <TopicCategoryChip
              category={topic.category}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{ flexShrink: 0 }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            fontWeight: 400,
          }}
        >
          {getTopicDisplayTitle(topic)}
        </Typography>
      </Box>
      <Divider />
    </>
  );
}
